/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { Text } from '@/shared/ui';
import Button from '@/shared/ui/components/basic/Button';
import { Switch } from '@/shared/ui/components/form/inputs/Switch';
import { cn } from '@/shared/ui/utils/tailwind';
import type { GoogleCalendarSettings } from '@services/requests/housien-services/CalendarService.calendars';
import type { GoogleSettingsPatch } from '@features/agenda/hooks/useGoogleSettings';
import { GroupLabel } from './GroupLabel';

type Translate = (key: string, vars?: Record<string, string>) => string;

export interface ConnectedSectionsProps {
  settings: GoogleCalendarSettings;
  /** Read-only links cannot write, so the whole push block is shut. */
  canWrite: boolean;
  personalCalendars: { calendar_id?: string; name?: string }[];
  t: Translate;
  onSave: (patch: GoogleSettingsPatch) => void;
}

/** A titled block inside the Google pane. */
const Block: React.FC<{
  title: string;
  subtitle?: string;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ title, subtitle, disabled = false, children }) => (
  <section
    aria-disabled={disabled || undefined}
    className={cn(
      'flex flex-col gap-(--space-3)',
      disabled && 'opacity-45',
    )}
  >
    <div className="flex flex-col gap-(--space-1)">
      <GroupLabel>{title}</GroupLabel>
      {subtitle && (
        <Text className="text-[length:var(--text-small)] text-[var(--text-secondary)] text-pretty">
          {subtitle}
        </Text>
      )}
    </div>
    {children}
  </section>
);

/**
 * Toggles one id in or out of a list without reordering the rest.
 *
 * @param ids - Current ids.
 * @param id - The id to flip.
 * @returns The next list.
 */
const toggled = (ids: string[], id: string): string[] =>
  ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];

/**
 * What the connected user can choose: what comes in, and what goes out.
 *
 * Every control saves on its own. A settings screen with a Save button
 * invites the user to change three things and lose two of them, and these
 * choices are each independent of the others.
 *
 * @param props - Settings, permissions and the save handler.
 * @returns The rendered sections.
 */
export const ConnectedSections: React.FC<ConnectedSectionsProps> = ({
  settings,
  canWrite,
  personalCalendars,
  t,
  onSave,
}) => (
  <>
    <Block
      title={t('integrations.import_title')}
      subtitle={t('integrations.import_subtitle')}
    >
      <div className="flex flex-col divide-y divide-[var(--border-subtle)]">
        {settings.google_calendars.map((calendar) => (
          <Switch
            key={calendar.id}
            checked={calendar.imported}
            label={calendar.name || calendar.id}
            description={
              calendar.access_role === 'reader'
                ? t('calendar_settings.access_read')
                : undefined
            }
            onChange={() =>
              onSave({
                import_calendar_ids: toggled(
                  settings.google_calendars
                    .filter((c) => c.imported)
                    .map((c) => c.id),
                  calendar.id,
                ),
              })
            }
          />
        ))}
      </div>
    </Block>

    <Block
      title={t('integrations.push_title')}
      subtitle={t('integrations.push_subtitle')}
      disabled={!canWrite}
    >
      <label className="flex flex-wrap items-center gap-(--space-3)">
        <Text
          component="span"
          className="text-[length:var(--text-body-sm)] text-[var(--text-primary)]"
        >
          {t('integrations.push_destination')}
        </Text>
        <select
          disabled={!canWrite}
          value={settings.destination_calendar_id}
          onChange={(e) => onSave({ destination_calendar_id: e.target.value })}
          className="h-[34px] rounded-(--radius-md) border border-[var(--border-medium)] bg-[var(--surface-raised)] px-(--space-3) text-[length:var(--text-small)] font-[weight:var(--font-medium)] text-[var(--text-primary)]"
        >
          <option value="">{t('integrations.push_destination_none')}</option>
          {settings.google_calendars
            .filter((c) => c.access_role !== 'reader')
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || c.id}
              </option>
            ))}
        </select>
      </label>

      <Switch
        checked={settings.push_personal_events}
        disabled={!canWrite}
        label={t('integrations.push_personal')}
        onChange={(checked) => onSave({ push_personal_events: checked })}
      />
      <div className="flex flex-wrap gap-(--space-2) pl-(--space-1)">
        {personalCalendars.map((calendar) => {
          const id = calendar.calendar_id ?? '';
          const on = settings.push_personal_calendar_ids.includes(id);
          return (
            <button
              key={id}
              type="button"
              aria-pressed={on}
              disabled={!canWrite}
              onClick={() =>
                onSave({
                  push_personal_calendar_ids: toggled(
                    settings.push_personal_calendar_ids,
                    id,
                  ),
                })
              }
              className={cn(
                'rounded-(--radius-full) border px-(--space-2-5) py-(--space-1)',
                'text-[length:var(--text-meta)] font-[weight:var(--font-medium)]',
                on
                  ? 'border-[var(--seed-base)] text-[var(--seed-base)]'
                  : 'border-[var(--border-subtle)] text-[var(--text-secondary)]',
              )}
            >
              {calendar.name}
            </button>
          );
        })}
      </div>

      <Switch
        checked={settings.push_visits}
        disabled={!canWrite}
        label={t('integrations.push_visits')}
        description={t('integrations.push_visits_detail')}
        onChange={(checked) => onSave({ push_visits: checked })}
      />
      <Switch
        checked={settings.push_edits_to_imported}
        disabled={!canWrite}
        label={t('integrations.push_edits')}
        onChange={(checked) => onSave({ push_edits_to_imported: checked })}
      />
      <Text className="text-[length:var(--text-meta)] text-[var(--text-tertiary-soft)] text-pretty">
        {t('integrations.conflict_note')}
      </Text>
    </Block>
  </>
);
